/**
 * useRateLimitTimer Hook
 *
 * Manages rate limit state with countdown timer for password-protected operations.
 * When rate limited (error code RATE_LIMITED), shows a 60-second countdown.
 */

import { useState, useEffect, useCallback, useRef } from 'react';

const RATE_LIMIT_DURATION = 60; // 60 seconds

interface UseRateLimitTimerReturn {
  /** Whether rate limit is active */
  isRateLimited: boolean;
  /** Seconds remaining until rate limit expires */
  secondsRemaining: number;
  /** Check if an error indicates rate limiting and start timer if so */
  handleError: (error: unknown) => string;
  /** Reset the rate limit state */
  reset: () => void;
}

/**
 * Check if error indicates rate limiting
 */
function isRateLimitError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;

  // Check for code property
  if ('code' in error && error.code === 'RATE_LIMITED') return true;

  // Check for message containing rate limit text
  if ('message' in error && typeof error.message === 'string') {
    const msg = error.message.toLowerCase();
    return msg.includes('too many') && (
      msg.includes('attempts') ||
      msg.includes('requests') ||
      msg.includes('try again')
    );
  }

  return false;
}

/**
 * Extract error message from error object
 */
function getErrorMessage(error: unknown, fallback: string): string {
  if (!error || typeof error !== 'object') return fallback;
  if ('message' in error && typeof error.message === 'string') {
    return error.message;
  }
  return fallback;
}

export function useRateLimitTimer(): UseRateLimitTimerReturn {
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // Start countdown timer
  const startTimer = useCallback(() => {
    // Clear existing timer if any
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    setIsRateLimited(true);
    setSecondsRemaining(RATE_LIMIT_DURATION);

    timerRef.current = setInterval(() => {
      setSecondsRemaining((prev) => {
        if (prev <= 1) {
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          setIsRateLimited(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  // Handle error and check for rate limiting
  const handleError = useCallback((error: unknown): string => {
    if (isRateLimitError(error)) {
      startTimer();
      return getErrorMessage(error, 'Too many attempts. Please wait and try again.');
    }
    return getErrorMessage(error, 'An error occurred');
  }, [startTimer]);

  // Reset rate limit state
  const reset = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setIsRateLimited(false);
    setSecondsRemaining(0);
  }, []);

  return {
    isRateLimited,
    secondsRemaining,
    handleError,
    reset,
  };
}

export default useRateLimitTimer;
